/**
 * Angler — anglerfish on the drop-off, where the light stops.
 *
 * WHERE THEY LIVE IS THE FEATURE. Everything else in this game happens in lit water: the
 * lagoon, the reef, the shallows. Past the shelf edge the bed falls away to −26 and the fog
 * closes, and until now that was somewhere the map ended rather than somewhere to go. Putting
 * the one animal in the game that carries its own light down there gives the dark a reason to
 * exist — you go over the edge because there is something to find, not because the terrain
 * happens to continue.
 *
 * THEY DO NOT CHASE. An anglerfish is an ambush animal: it hangs, it jigs the lure, it waits.
 * That is also why it works here — a creature that pursued the player would turn the drop-off
 * into a threat, and there is nothing in this game to be threatened by. It ignores you
 * completely, which is somehow worse.
 *
 * THE LURE IS A REAL LIGHT, not an emissive dot. See render/LureLight: the nearest lit esca is
 * pushed to the seafloor and to every shark in the water, so swimming up to one lights your own
 * flank. That is the whole reason to build this animal rather than any other.
 */
import * as THREE from 'three';
import { Rng } from '../core/Rng.ts';
import { AnglerMaterial } from '../render/AnglerMaterial.ts';
import { MOBILE } from '../data/quality.ts';
import { bedHeight, waterDepth, COAST } from '../world/Coast.ts';
import { buildAngler, escaLocal } from '../world/angler/voxelAngler.js';
import type { LureState } from '../render/LureLight.ts';
import type { SkyEnv } from '../world/DayNight.ts';

/**
 * Generator voxel pitch, model units. Times the ~2.3 scale below, this lands at 0.046 u a
 * voxel — the same grain as the manta and the turtle, so the animal belongs to the same world.
 *
 * Cheap enough not to need a mobile tier of its own: 2.8k voxels against the ray's 16k, because
 * an anglerfish is a compact lump and a manta is mostly wing.
 */
const VOX = MOBILE ? 0.028 : 0.020;
/** the model runs -0.52..0.42 along its spine */
const MODEL_LEN = 0.94;
/** world length range. A real Melanocetus is 18 cm; this is not, and it is better for it. */
const LEN_RANGE: [number, number] = [1.7, 2.6];

/** how far along the coast they are kept, and how far out */
const WINDOW_X = 300;
/**
 * Cross-shore band.
 *
 * It started at the shelf edge and ran out over the plain, which put every one of them a long
 * swim across featureless dark — you could see a speck and never reach it. Now it starts on the
 * FORE-REEF SLOPE, where the reef ends and people already swim, and runs out through the
 * seamounts. So the first one you meet is at the edge of familiar water and the rest are
 * reasons to keep going.
 */
const BAND_Z: [number, number] = [COAST.reefTo + 6, COAST.offshoreFrom + 96];

/**
 * How far a lure throws, and how bright.
 *
 * Both raised hard from the first pass. At 11 u of reach the pool of light on the sand was
 * smaller than the animal, so from any distance the lure was a single bright pixel —
 * indistinguishable from the marine snow drifting past it. The light has to be big enough to
 * see as a light before you are close enough to see what is holding it.
 */
const LURE_REACH = 26;
const LURE_POWER = 4.2;
/** beyond this the lure stops being pushed to the world's materials; it still glows */
const LURE_CUTOFF = 90;

/** the halo's radius, world units — the visible size of the glow, not of the bulb */
const HALO_SIZE = 3.4;

interface Pose { jaw: number; sway: number; rodRate: number; swim: number; drift: number }
const BEH: Record<string, Pose> = {
  lurk: { jaw: 0.03, sway: 0.35, rodRate: 0.55, swim: 0.25, drift: 0.05 },
  jig: { jaw: 0.06, sway: 1.55, rodRate: 2.10, swim: 0.35, drift: 0.04 },
  gape: { jaw: 1.00, sway: 0.60, rodRate: 1.10, swim: 0.60, drift: 0.10 },
};
/* Weighted hard to lurking, because that is what an ambush predator does. The gape is rare and
   short — a mouth that hangs open stops being a shock the second time you see it. */
const ORDER = ['lurk', 'jig', 'lurk', 'lurk', 'jig', 'gape', 'lurk', 'jig'];
const DWELL: Record<string, [number, number]> = {
  lurk: [9, 20], jig: [7, 14], gape: [1.6, 3.0],
};

interface Individual {
  root: THREE.Group;
  mesh: THREE.InstancedMesh;
  material: AnglerMaterial;
  halo: THREE.Mesh;
  scale: number;
  p: THREE.Vector3;
  heading: number;
  beh: string;
  behIdx: number;
  dwell: number;
  live: Pose;
  rodPhase: number;
  swimPhase: number;
  pulse: number;
  /** slow wander around a held station */
  homeX: number;
  homeZ: number;
  bobA: number;
  bobB: number;
  t: number;
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const ESCA = new Float32Array(3);
const V = new THREE.Vector3();

export class Angler {
  readonly group = new THREE.Group();

  private readonly individuals: Individual[] = [];
  private readonly rng: Rng;
  private readonly geometry: THREE.BufferGeometry;
  private readonly rodRoot: number[];
  /** one billboarded disc per angler, so the lure reads as a light and not as a bright pixel */
  private readonly haloGeo: THREE.PlaneGeometry;
  private readonly haloMat: THREE.ShaderMaterial;
  readonly voxels: number;

  constructor(count = 5, seed = 0xa2f1e) {
    this.rng = new Rng(seed >>> 0);
    this.group.name = 'anglers';

    const D = buildAngler({ vox: VOX, seed: this.rng.int(1, 1 << 28), girth: 1, teeth: 1 });
    this.voxels = D.n;
    this.rodRoot = [D.rodRoot[0], D.rodRoot[1], D.rodRoot[2]];

    /* One geometry, shared. Every individual is the same animal at a different size and phase,
       which for something this rarely seen up close is indistinguishable from three of them. */
    this.geometry = new THREE.BoxGeometry(1, 1, 1);
    const uv = new Float32Array(D.n * 3);
    const col = new Float32Array(D.n * 3);
    for (let i = 0; i < D.n; i++) {
      uv[i * 3] = D.u[i]; uv[i * 3 + 1] = D.v[i]; uv[i * 3 + 2] = D.w[i];
      // Display space in, linear out: this pipeline shades in linear with ACES on the end.
      col[i * 3] = toLinear(D.cr[i]);
      col[i * 3 + 1] = toLinear(D.cg[i]);
      col[i * 3 + 2] = toLinear(D.cbl[i]);
    }
    this.geometry.setAttribute('aUV', new THREE.InstancedBufferAttribute(uv, 3));
    this.geometry.setAttribute('aCol', new THREE.InstancedBufferAttribute(col, 3));
    this.geometry.setAttribute('aPart', new THREE.InstancedBufferAttribute(D.part, 1));
    this.geometry.setAttribute('aS', new THREE.InstancedBufferAttribute(D.s, 1));
    this.geometry.setAttribute('aRole', new THREE.InstancedBufferAttribute(D.role, 1));
    this.geometry.setAttribute('aShade', new THREE.InstancedBufferAttribute(D.shade, 1));
    this.geometry.setAttribute('aGlow', new THREE.InstancedBufferAttribute(D.glow, 1));

    /* THE HALO IS WHY IT READS. An esca is a couple of centimetres across, so however hard it
       burns it is a pixel or two on screen — and a bright pixel in a dark frame is exactly what
       marine snow looks like. There is no bloom pass in this game to spread it, so the glow is
       drawn explicitly: a camera-facing disc with a soft falloff, additive, depth-tested so the
       animal's own body occludes it from behind. */
    this.haloGeo = new THREE.PlaneGeometry(1, 1);
    this.haloMat = new THREE.ShaderMaterial({
      name: 'AnglerHalo',
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      fog: false,
      uniforms: {
        uSize: { value: HALO_SIZE },
        uCol: { value: new THREE.Vector3(0.42, 1.0, 0.86) },
        uPower: { value: 1 },
      },
      vertexShader: /* glsl */ `
        uniform float uSize;
        varying vec2 vUv;
        void main() {
          vUv = uv;
          // Billboarded in VIEW space: the offset is applied after the model-view transform,
          // so the disc always faces the camera whatever the animal is doing.
          vec4 mv = modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0);
          mv.xy += position.xy * uSize;
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uCol;
        uniform float uPower;
        varying vec2 vUv;
        void main() {
          float r = length(vUv - 0.5) * 2.0;
          /* A high power on the falloff, so the disc has a bright core and a long faint skirt
             rather than a visible rim. A linear falloff reads as a sticker. */
          float a = pow(max(0.0, 1.0 - r), 3.2);
          gl_FragColor = vec4(uCol * uPower * a * 1.6, a);
        }
      `,
    });

    const identity = new THREE.Matrix4();
    for (let i = 0; i < count; i++) {
      const scale = this.rng.range(LEN_RANGE[0], LEN_RANGE[1]) / MODEL_LEN;
      const material = new AnglerMaterial(VOX);
      material.uniforms.uHinge.value.set(D.hinge[0], D.hinge[1], D.hinge[2]);
      material.uniforms.uRodRoot.value.set(D.rodRoot[0], D.rodRoot[1], D.rodRoot[2]);
      material.uniforms.uRodLen.value = this.rng.range(0.5, 0.78);
      material.uniforms.uRodArc.value = this.rng.range(1.0, 1.5);

      const mesh = new THREE.InstancedMesh(this.geometry, material, D.n);
      for (let k = 0; k < D.n; k++) mesh.setMatrixAt(k, identity);
      mesh.instanceMatrix.needsUpdate = true;
      mesh.frustumCulled = false;

      const root = new THREE.Group();
      root.scale.setScalar(scale);
      root.rotation.order = 'YXZ';
      root.add(mesh);
      this.group.add(root);

      /* The halo hangs in WORLD space rather than under the animal's group: it is scaled in
         view units and must not inherit the individual's own scale, or a big angler would get
         a glow twice the size of a small one for no reason. */
      const halo = new THREE.Mesh(this.haloGeo, this.haloMat.clone());
      halo.frustumCulled = false;
      halo.renderOrder = 6;
      this.group.add(halo);

      const homeX = this.rng.range(-WINDOW_X * 0.5, WINDOW_X * 0.5);
      const homeZ = this.rng.range(BAND_Z[0], BAND_Z[1]);
      this.individuals.push({
        root, mesh, material, scale, halo,
        p: new THREE.Vector3(homeX, 0, homeZ),
        heading: this.rng.range(0, Math.PI * 2),
        beh: 'lurk', behIdx: 0, dwell: this.rng.range(4, 14),
        live: { ...BEH.lurk },
        rodPhase: this.rng.range(0, Math.PI * 2),
        swimPhase: this.rng.range(0, Math.PI * 2),
        pulse: this.rng.range(0, Math.PI * 2),
        homeX, homeZ,
        bobA: this.rng.range(0.10, 0.26),
        bobB: this.rng.range(0.07, 0.19),
        t: this.rng.range(0, 100),
      });
    }
  }

  /**
   * The nearest lure worth lighting the world with, or power 0 if none is close.
   *
   * Nearest ONE, deliberately — see render/LureLight. A loop over every angler in every
   * fragment shader in the game would be paid on every pixel of every frame, for a light that
   * is off screen almost always.
   */
  nearestLure(x: number, z: number, out: LureState): LureState {
    let best = -1;
    let bestD = LURE_CUTOFF * LURE_CUTOFF;
    for (let i = 0; i < this.individuals.length; i++) {
      const a = this.individuals[i];
      const dx = a.p.x - x, dz = a.p.z - z;
      const d = dx * dx + dz * dz;
      if (d < bestD) { bestD = d; best = i; }
    }
    if (best < 0) {
      out.power = 0;
      return out;
    }
    const a = this.individuals[best];
    escaLocal(this.rodRoot, a.material.uniforms.uRodLen.value as number,
      a.material.uniforms.uRodArc.value as number, a.live.sway, a.rodPhase, ESCA);
    V.set(ESCA[0], ESCA[1], ESCA[2]);
    a.root.localToWorld(V);
    out.x = V.x; out.y = V.y; out.z = V.z;
    out.reach = LURE_REACH;
    /* Fades with distance rather than switching off, so walking out of range is not a light
       going out. The pulse rides along, so the sand breathes with the lure. */
    const fall = 1 - Math.min(1, Math.sqrt(bestD) / LURE_CUTOFF);
    out.power = LURE_POWER * fall * fall * pulseOf(a);
    return out;
  }

  update(dt: number, time: number, focusX: number): void {
    for (const a of this.individuals) {
      a.t += dt;
      a.dwell -= dt;
      if (a.dwell <= 0) {
        a.behIdx = (a.behIdx + 1) % ORDER.length;
        a.beh = ORDER[a.behIdx];
        const w = DWELL[a.beh];
        a.dwell = this.rng.range(w[0], w[1]);
      }
      const T = BEH[a.beh];
      const k = 1 - Math.exp(-2.2 * dt);
      // The jaw opens fast and closes slowly, which is what a strike looks like.
      const jk = 1 - Math.exp(-(T.jaw > a.live.jaw ? 9.0 : 2.2) * dt);
      a.live.jaw = lerp(a.live.jaw, T.jaw, jk);
      a.live.sway = lerp(a.live.sway, T.sway, k);
      a.live.rodRate = lerp(a.live.rodRate, T.rodRate, k);
      a.live.swim = lerp(a.live.swim, T.swim, k);
      a.live.drift = lerp(a.live.drift, T.drift, k);

      /* Phases INTEGRATED, never uTime times a live rate: absolute time scaled by a changing
         rate jumps by uTime*deltaRate the moment the rate moves, and uTime only grows. */
      a.rodPhase += dt * a.live.rodRate * 1.7;
      a.swimPhase += dt * (0.35 + a.live.swim * 1.1);
      a.pulse += dt * 1.9;

      /* Hangs. It holds a station and breathes around it rather than swimming anywhere — the
         drift is small enough that it reads as neutral buoyancy, not as travel. */
      const wander = a.live.drift;
      a.p.x = a.homeX + Math.sin(a.t * 0.21 + a.bobA * 10) * 2.4 * wander * 10;
      a.p.z = a.homeZ + Math.cos(a.t * 0.17 + a.bobB * 10) * 1.9 * wander * 10;

      /* Streamed along the coast, like everything else: an animal that stayed at its birth X
         would be left behind the moment the player swam anywhere. */
      const span = WINDOW_X;
      while (a.homeX - focusX > span * 0.5) a.homeX -= span;
      while (a.homeX - focusX < -span * 0.5) a.homeX += span;

      const bed = bedHeight(a.p.x, a.p.z);
      const depth = Math.max(1, waterDepth(a.p.x, a.p.z));
      /* Just off the bottom, and the bottom now includes the seamounts — so they hang on the
         flanks of the mounts as well as out on the plain, which is where an ambush animal
         would actually be. They sit low on purpose: the lure is meant to be the only thing
         above them, and a fish in mid-water has nothing to be silhouetted against. */
      a.p.y = bed + Math.min(2.6, depth * 0.22) + Math.sin(a.t * 0.5 + a.bobA * 9) * 0.28;

      a.heading += Math.sin(a.t * 0.11 + a.bobB * 7) * dt * 0.25;
      a.root.position.copy(a.p);
      a.root.rotation.y = a.heading;
      a.root.rotation.x = Math.sin(a.t * 0.3) * 0.06;

      /* The halo goes exactly where the light is, which means walking the same arc the vertex
         shader does. One evaluation, reused for both. */
      escaLocal(this.rodRoot, a.material.uniforms.uRodLen.value as number,
        a.material.uniforms.uRodArc.value as number, a.live.sway, a.rodPhase, ESCA);
      V.set(ESCA[0], ESCA[1], ESCA[2]);
      a.root.localToWorld(V);
      a.halo.position.copy(V);
      (a.halo.material as THREE.ShaderMaterial).uniforms.uPower.value = pulseOf(a) * 1.15;

      const u = a.material.uniforms;
      u.uJaw.value = a.live.jaw;
      u.uRodSway.value = a.live.sway;
      u.uRodPhase.value = a.rodPhase;
      u.uSwim.value = a.swimPhase;
      u.uSwimAmp.value = 0.4 + a.live.swim;
      u.uPulse.value = pulseOf(a);
      u.uTime.value = time;
    }
  }

  setEnv(env: SkyEnv): void {
    for (const a of this.individuals) a.material.setEnv(env);
  }

  dispose(): void {
    for (const a of this.individuals) {
      a.material.dispose();
      (a.halo.material as THREE.ShaderMaterial).dispose();
    }
    this.haloGeo.dispose();
    this.haloMat.dispose();
    this.geometry.dispose();
    this.group.removeFromParent();
  }
}

/**
 * The lure's brightness right now.
 *
 * A slow breath with a fast twinkle on it. Real bioluminescence is not a steady lamp, and a
 * steady one down here would read as a machine — which is exactly the wrong note for the only
 * living light in the game.
 */
function pulseOf(a: Individual): number {
  return 0.78 + 0.22 * Math.sin(a.pulse) + 0.07 * Math.sin(a.pulse * 5.9);
}

function toLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
